import React, { useEffect, useState } from "react";
import { CreditCard, Loader2, Landmark, CheckCircle2, ShieldCheck, XCircle, CalendarDays, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { endpoints } from "@/lib/api";

const money = (n, cur = "ZAR") => {
  const sym = cur === "ZAR" ? "R" : cur === "USD" ? "$" : cur === "EUR" ? "€" : cur === "GBP" ? "£" : "";
  return `${sym}${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

export default function ClientSubscriptionPanel({ clientSlug, clientName }) {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    debit_account_holder: "",
    debit_bank_name: "",
    debit_account_number: "",
    debit_branch_code: "",
    debit_account_type: "cheque",
    debit_day: 1,
    debit_reference: "",
    authorize: false,
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const s = await endpoints.getClientSubscription(clientSlug);
      setSub(s);
      if (s) {
        setForm((f) => ({
          ...f,
          debit_account_holder: s.debit_account_holder || clientName || "",
          debit_bank_name: s.debit_bank_name || "",
          debit_account_number: s.debit_account_number || "",
          debit_branch_code: s.debit_branch_code || "",
          debit_account_type: s.debit_account_type || "cheque",
          debit_day: s.debit_day || 1,
          debit_reference: s.debit_reference || `ISO-${(clientSlug || "").slice(0, 10).toUpperCase()}`,
          authorize: !!s.debit_authorized,
        }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load subscription");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientSlug]);

  const submit = async () => {
    if (!form.authorize) return toast.error("Please tick the authorization checkbox");
    for (const k of ["debit_account_holder", "debit_bank_name", "debit_account_number"]) {
      if (!form[k]?.trim()) return toast.error(`${k.replace("debit_", "").replace("_", " ")} is required`);
    }
    setBusy(true);
    try {
      await endpoints.setDebitMandate(clientSlug, { ...form, debit_day: Number(form.debit_day) });
      toast.success("Debit order mandate saved — thank you");
      setEditing(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save mandate"); }
    finally { setBusy(false); }
  };

  const cancelMandate = async () => {
    if (!window.confirm("Cancel the debit order mandate? ISOQuest will no longer debit your account automatically.")) return;
    try {
      await endpoints.cancelDebitMandate(clientSlug);
      toast.success("Mandate cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading subscription…</div>;

  if (!sub) {
    return (
      <div className="glass-card rounded-3xl p-8 text-center" data-testid="no-subscription">
        <div className="mx-auto h-12 w-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-3"><CreditCard className="h-5 w-5" /></div>
        <h3 className="font-display font-bold">No subscription yet</h3>
        <p className="text-sm text-slate-500 mt-1">Your ISOQuest administrator will enrol you in a plan shortly. You'll be able to set up your debit order from here once that's done.</p>
      </div>
    );
  }

  const showForm = editing || !sub.debit_authorized;

  return (
    <div className="space-y-5">
      {/* Plan summary */}
      <div className="glass-card rounded-3xl p-5" data-testid="sub-summary">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-indigo-500 font-semibold">Your plan</div>
            <h3 className="font-display text-xl font-extrabold mt-1">{sub.plan_name || "Custom plan"}</h3>
            <div className="text-sm text-slate-500 mt-1">
              <b>{money(sub.price, sub.currency)}</b> per {sub.interval === "annual" ? "year" : "month"} · status <b>{sub.status}</b>
            </div>
          </div>
          <div className="text-right text-xs text-slate-500 space-y-1">
            {sub.next_billing_date && <div className="flex items-center gap-1 justify-end"><CalendarDays className="h-3 w-3" /> Next debit <b className="ml-1 text-slate-700">{sub.next_billing_date}</b></div>}
            {sub.last_paid_date && <div>Last paid {sub.last_paid_date}</div>}
          </div>
        </div>
      </div>

      {/* Mandate state */}
      <div className="glass-card rounded-3xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Landmark className="h-4 w-4" /></span>
            <div>
              <h3 className="font-display font-bold">Debit order mandate</h3>
              <p className="text-xs text-slate-500">Set the day of the month you'd like us to debit for your subscription.</p>
            </div>
          </div>
          {sub.debit_authorized && !editing && (
            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-full">
              <ShieldCheck className="h-3 w-3 mr-1" /> Authorized
            </Badge>
          )}
        </div>

        {sub.debit_authorized && !editing ? (
          <div className="grid sm:grid-cols-2 gap-3 text-sm" data-testid="mandate-active">
            <Row k="Account holder" v={sub.debit_account_holder || "—"} />
            <Row k="Bank" v={sub.debit_bank_name || "—"} />
            <Row k="Account number" v={sub.debit_account_number_masked || sub.debit_account_number || "—"} mono />
            <Row k="Branch code" v={sub.debit_branch_code || "—"} mono />
            <Row k="Account type" v={(sub.debit_account_type || "").replace(/^./, (c) => c.toUpperCase()) || "—"} />
            <Row k="Debit day" v={sub.debit_day ? `${sub.debit_day}${suffix(sub.debit_day)} of each month` : "—"} />
            {sub.debit_reference && <Row k="Reference" v={sub.debit_reference} mono />}
            {sub.debit_authorized_at && <Row k="Authorized" v={new Date(sub.debit_authorized_at).toLocaleDateString()} />}
            <div className="sm:col-span-2 flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-mandate"><Pencil className="h-4 w-4 mr-1" /> Edit details</Button>
              <Button variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={cancelMandate} data-testid="cancel-mandate"><XCircle className="h-4 w-4 mr-1" /> Cancel mandate</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="mandate-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Account holder (name on account)">
                <Input data-testid="mandate-holder" value={form.debit_account_holder} onChange={(e) => setForm({ ...form, debit_account_holder: e.target.value })} />
              </Field>
              <Field label="Bank name">
                <Input data-testid="mandate-bank" value={form.debit_bank_name} onChange={(e) => setForm({ ...form, debit_bank_name: e.target.value })} placeholder="e.g. First National Bank" />
              </Field>
              <Field label="Account number">
                <Input data-testid="mandate-number" value={form.debit_account_number} onChange={(e) => setForm({ ...form, debit_account_number: e.target.value })} />
              </Field>
              <Field label="Branch code">
                <Input data-testid="mandate-branch" value={form.debit_branch_code} onChange={(e) => setForm({ ...form, debit_branch_code: e.target.value })} />
              </Field>
              <Field label="Account type">
                <Select value={form.debit_account_type} onValueChange={(v) => setForm({ ...form, debit_account_type: v })}>
                  <SelectTrigger data-testid="mandate-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cheque">Cheque / Current</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                    <SelectItem value="transmission">Transmission</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Debit day of the month">
                <Select value={String(form.debit_day)} onValueChange={(v) => setForm({ ...form, debit_day: Number(v) })}>
                  <SelectTrigger data-testid="mandate-day"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map((d) => <SelectItem key={d} value={String(d)}>{d}{suffix(d)} of each month</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Reference (shown on your statement)">
                <Input data-testid="mandate-ref" value={form.debit_reference} onChange={(e) => setForm({ ...form, debit_reference: e.target.value })} />
              </Field>
            </div>

            <label className="flex items-start gap-3 text-xs text-slate-600 bg-indigo-50/60 border border-indigo-100 rounded-xl p-3 cursor-pointer">
              <Checkbox checked={form.authorize} onCheckedChange={(v) => setForm({ ...form, authorize: !!v })} data-testid="mandate-authorize" className="mt-0.5" />
              <span>
                I authorize ISOQuest to collect <b>{money(sub.price, sub.currency)}</b> from the account above on the {form.debit_day}{suffix(form.debit_day)} of each {sub.interval === "annual" ? "year" : "month"} starting on <b>{sub.next_billing_date || "the next billing date"}</b>, until I cancel this mandate from my portal.
              </span>
            </label>

            <div className="flex items-center gap-2">
              <Button className="brand-gradient text-white" disabled={busy || !form.authorize} onClick={submit} data-testid="mandate-submit">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-1" />} {sub.debit_authorized ? "Save changes" : "Authorize debit order"}
              </Button>
              {sub.debit_authorized && editing && <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (<div><Label className="text-xs">{label}</Label><div className="mt-1">{children}</div></div>);
const Row = ({ k, v, mono }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div><div className={mono ? "font-mono" : ""}>{v}</div></div>
);
const suffix = (n) => (n % 10 === 1 && n !== 11 ? "st" : n % 10 === 2 && n !== 12 ? "nd" : n % 10 === 3 && n !== 13 ? "rd" : "th");
